import { LimitedRollout, PluginToInitialize } from "./types";
import { logger } from "../debug";
import type ConfigType from "config:./defaultValues";

export function isInTest(
  percentage: LimitedRollout[0],
  exp?: LimitedRollout[1],
  s1var?: LimitedRollout[2],
  force?: boolean
) {
  // Valid type passed (since using TS shouldn't be a problem but rather safe than sorry)
  if (
    Array.isArray(percentage) &&
    !percentage.every((num) => typeof num === "number")
  ) {
    throw new Error("Expected range, represented by an 2 tuple of floats");
  } else if (typeof percentage !== "number") {
    throw new Error("Expect exclusive max percentage represented by a float");
  }

  let result = true;
  let currentExperience = localStorage.getItem(exp);
  if (currentExperience !== null) {
    let positive = typeof s1var === "function" ? s1var(true) : s1var;

    result = positive === currentExperience ? true : false;
  } else {
    const randomPercentage = Math.random();
    if (
      Array.isArray(percentage) &&
      percentage[0] < randomPercentage &&
      percentage[1] > randomPercentage
    )
      result = false;
    if (percentage > randomPercentage) result = false;
  }

  if (s1var && exp) {
    if (currentExperience === null || force == true) {
      currentExperience = typeof s1var === "function" ? s1var(result) : s1var;
      localStorage.setItem(exp, currentExperience);
    }

    let currentS1 = sessionStorage.getItem("aLaunch_eidPre");
    // TODO: Add _satellite to global
    // @ts-ignore
    if (window?._satellite?.track) {
      // @ts-ignore
      window._satellite.track("target-A4L", { list1: currentExperience });
    } else if (currentS1 === null || !currentS1?.includes(currentExperience)) {
      if (currentS1) {
        currentS1 += `|${currentExperience}`;
      } else {
        currentS1 = currentExperience;
      }
      sessionStorage.setItem("aLaunch_eidPre", currentS1);
    }
  }

  return result;
}

// I'd rather this be chunk loaded than a direct dependency
let cookiesImport = import("./cookies");

export function initializePlugins(
  pluginConfig: Record<PropertyKey, PluginToInitialize>,
  defaultValues: typeof ConfigType,
  queryParams?: URLSearchParams
) {
  return Object.keys(pluginConfig).map((plugin) => {
    const {
      pluginFunction,
      percentage,
      isAllowed,
      consentLevel,
      enabled,
      debug,
      simulate = true,
      blockWithGPC = false,
      ...otherValues
    } = pluginConfig[plugin];

    const pluginQueryParam = queryParams.get(plugin);
    // Using query param to override plugin enabled state
    let pluginOverride: boolean | undefined;
    if (pluginQueryParam === "true") {
      pluginOverride = true;
    } else if (pluginQueryParam === "false") {
      pluginOverride = false;
    }
    // Will allow us to force an experience
    const inExperience =
      queryParams.get(plugin) !== null
        ? isInTest(
            pluginOverride ? 1 : 0,
            percentage[1],
            typeof percentage[2] === "function"
              ? percentage[2](pluginOverride)
              : percentage[2],
            true
          )
        : percentage !== undefined
          ? isInTest(...percentage)
          : true;
    const isAllowedResult = isAllowed?.() ?? true;
    if (debug) {
      logger.debug(plugin, {
        enabled,
        inExperience,
        isAllowedResult,
        pluginOverride,
      });
    }
    const newPlugin = pluginFunction({
      name: plugin,
      enabled:
        pluginOverride !== undefined
          ? pluginOverride
          : (enabled ?? false) && inExperience && isAllowedResult,
      debug: debug ?? false,
      isAllowed,
      simulate,
      ...otherValues,
    });

    if (newPlugin.config === undefined) {
      newPlugin.config = {
        // @ts-ignore
        simulate,
        blockWithGPC,
        ...otherValues,
      };
    } else {
      // Inject required config values into the plugin config
      newPlugin.config.blockWithGPC = blockWithGPC;
      newPlugin.config.simulate = simulate;
    }

    async function consentCheck ({ abort, instance, }) {
      const currentConsentMap = (await cookiesImport).getConsentMap();
      const pluginConsent =
        (typeof consentLevel === "function"
          ? consentLevel?.(currentConsentMap)
          : consentLevel) ?? false;
       if (!pluginConsent || (newPlugin.config?.blockWithGPC && navigator?.["globalPrivacyControl"])) {
        // This will ensure that nothing is loaded and no subquent actions are executed for this plugin
        await instance.plugins.disable(plugin);
        return abort("Consent is missing for [" + plugin + "]");
      }
    };

    const functionName = simulate === true ? `initializeStart:${plugin}` : "initializeStart";
    // This will not work if used with addPlugin as :[plugin] cannot refer to it-self
    newPlugin[functionName] = consentCheck;
    return newPlugin;
  });
}
